import type { MouseEvent } from "react";
import { useNavigate } from "react-router";
import { BookmarkCheck, BookmarkPlus } from "lucide-react";
import RemixMark from "@/app/routes/dashboard/components/icons/RemixMark";
import { queue } from "@/components/Toast";
import type { Trend } from "@/types/trend.type";
import {
  REFERENCES_PATH,
  saveErrorMessage,
  showAlreadySaved,
  useSavedTrendIds,
  useSaveTarget,
  useSaveTrendToReferences,
} from "../api/useSaveTrendToReferences";
import { usePermissions } from "@/hooks/usePermissions";
import { recreatePrompt } from "./trendMeta";

/**
 * Act from the plate: Save and Recreate, in media glass at the foot of the
 * frame, without opening the trend first.
 *
 * Save files the trend itself, as it is (useSaveTrendToReferences).
 *
 * Optimistic, as every mutation here is: the button reads "Saved" the moment it
 * is pressed, and goes back, with the error toast, if the save fails.
 *
 * Both are controls of their own sitting over the card's hit area, so a click
 * stops here and never opens the detail.
 */
export default function PlateActions({
  trend,
  live,
}: {
  trend: Trend;
  /** False in the fixture harness, which has no organization to save into: a
   *  live save there would POST to the real API for a route with no org. */
  live: boolean;
}) {
  const navigate = useNavigate();
  const save = useSaveTrendToReferences();
  const target = useSaveTarget();
  const saved = useSavedTrendIds().includes(trend.id);
  // Recreate opens a Workshop project, which Viewers can't create.
  const { canUseWorkshop } = usePermissions();

  const canSave = live && target !== null;

  const onSave = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (saved) {
      showAlreadySaved(() => navigate(REFERENCES_PATH));
      return;
    }
    save.mutateAsync(trend).catch((error: unknown) => {
      queue.add({ title: "Could not save that", description: saveErrorMessage(error), variant: "error" });
    });
  };

  /* The same hand-off the panel's Recreate makes: a prepared draft carried into
     Workshop through navigation state, and nothing saved or sent from here. */
  const onRecreate = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    navigate("/dashboard/workshop", { state: { draftPrompt: recreatePrompt(trend) } });
  };

  return (
    <div className="trend-actions">
      {canSave ? (
        <button
          type="button"
          className="co-media-chip co-media-chip--label"
          onClick={onSave}
          aria-label={saved ? `${trend.title} is saved to references` : `Save ${trend.title} to references`}
        >
          {saved ? (
            <BookmarkCheck className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
          ) : (
            <BookmarkPlus className="size-[13px]" strokeWidth={2.25} aria-hidden="true" />
          )}
          {saved ? "Saved" : "Save"}
        </button>
      ) : null}
      {canUseWorkshop ? (
        <button
          type="button"
          className="co-media-chip co-media-chip--label"
          onClick={onRecreate}
          aria-label={`Recreate ${trend.title} in Workshop`}
        >
          <RemixMark className="size-[13px]" aria-hidden="true" />
          Recreate
        </button>
      ) : null}
    </div>
  );
}
